import React, { memo } from 'react';
import cn from 'classnames';

import router from 'next/router';
import useSWR from 'swr';
import { useTranslation } from 'next-i18next';
import { AxiosResponse } from 'axios';
import { ButtonV3 } from 'components/ComponentV2';
import { Row } from 'components/layout';
import Avatar from 'components/Avatar';
import { RightSidebarSkeleton } from 'components/skeletons/dashboard.component';
import { useFilteredTouchpoints } from 'hooks/useFilteredTouchpoints';
import { PAGE_ROUTES } from 'lib/page-routes';
import { API_ROUTES } from 'lib/api-routes';
import { StudentSocietiesResponse } from 'lib/models/student-society';
import { parseAxiosArrayResponseV2 } from 'lib/utils/parser';
import { apiInstance } from 'lib/utils/axios';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import styles from './SidebarRight.module.scss';

export interface SidebarRightProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const SidebarRight: React.FC<SidebarRightProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: SidebarRightProps) => {
    const contentClassNames = cn(
      {
        [styles.SidebarRight]: true,
      },
      className
    );
    const [t] = useTranslation('candidate-dashboard');

    const {
      appliedTouchpoints,
      acceptedTouchpoints,
      followedCompanies,
      followedSocieties,
      isFollowedCompaniesLoading,
      isFollowedSocietiesLoading,
    } = useFilteredTouchpoints();
    function EventDate(startDate: string) {
      const today = new Date();
      const eventDate = new Date(startDate);

      if (eventDate.toDateString() === today.toDateString()) {
        return (
          <p>
            {eventDate.getUTCHours()}:
            {eventDate.getUTCMinutes() == 0 ? '00' : eventDate.getUTCMinutes()}
          </p>
        );
      } else if (
        eventDate.toDateString() ===
        new Date(today.getTime() + 86400000).toDateString()
      ) {
        return <p>Tomorrow</p>;
      }
    }

    const pathname = `${API_ROUTES.STUDENT_SOCIETIES}${API_ROUTES.RECOMMENDED}`;

    const { data: similarSocietiesResponse } = useSWR<
      AxiosResponse<StudentSocietiesResponse>
    >(pathname, apiInstance.get, {
      revalidateOnFocus: false,
    });
    const similarSocieties = parseAxiosArrayResponseV2(
      similarSocietiesResponse
    );

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="SidebarRight"
      >
        <section className={styles.section}>
          <Row className={styles.sectionHeader}>
            <p>{t('sidebar-right.communities')}</p>
          </Row>
          {!isFollowedSocietiesLoading ? (
            <Row className={styles.sectionwrap}>
              {followedSocieties?.length > 0 ? (
                followedSocieties.slice(0, 5).map((society, index) => (
                  <Row
                    align="start"
                    justify="space-between"
                    wrap={false}
                    columnGap={8}
                    className={styles.sectionRow}
                    key={index}
                    onClick={() =>
                      router.push(`${PAGE_ROUTES.SOCIETIES}/${society.slug}`)
                    }
                  >
                    <section className={styles.sectionRowContainer}>
                      <Avatar
                        name={society.name}
                        icon={society.icon_url}
                        size="small"
                        className={styles.avatar}
                        altText={'society_logo'}
                      />
                      <p className={styles.sectionText}>{society.name}</p>
                    </section>
                    {society.unseen_count > 0 && (
                      <span className={styles.countText}>
                        <p>{society.unseen_count}</p>
                      </span>
                    )}
                  </Row>
                ))
              ) : (
                <Row className={styles.emptyRow}>
                  <div className={styles.cont}>
                    <div className={styles.emptyTextWrapper}>
                      <div className={styles.emptyContainer}>
                        <p className={styles.emptyHeadingText}>
                          {t('sidebar-right.empty-community-heading')}
                        </p>
                        <p className={styles.emptyText}>
                          {t('sidebar-right.empty-communitiy-desc')}
                        </p>
                      </div>
                      <div className={styles.sectionRowEmpty}>
                        {similarSocieties.slice(0, 3).map((society, index) => {
                          const {
                            attributes: { slug, name, icon_url },
                          } = society;

                          return (
                            <Row
                              align="start"
                              justify="space-between"
                              wrap={false}
                              columnGap={8}
                              key={index}
                              onClick={() =>
                                router.push(`${PAGE_ROUTES.SOCIETIES}/${slug}`)
                              }
                            >
                              <section className={styles.sectionRowContainer}>
                                <Avatar
                                  name={name}
                                  icon={icon_url}
                                  size="small"
                                  className={styles.avatar}
                                  altText={'society_logo'}
                                />
                                <p className={styles.sectionText}>{name}</p>
                              </section>
                            </Row>
                          );
                        })}
                      </div>

                      <p
                        onClick={() =>
                          router.push(PAGE_ROUTES.SOCIETIES_SEARCH)
                        }
                        className={styles.exploreButton}
                      >
                        Explore communities
                      </p>
                    </div>
                  </div>
                </Row>
              )}
              {/* societies list */}
              {followedSocieties?.length > 0 && (
                <Row className={styles.sectionLastRow}>
                  {followedSocieties?.length > 2 && (
                    <ButtonV3
                      className={styles.sectionButton}
                      variant="text"
                      onClick={() =>
                        router.push(
                          `${PAGE_ROUTES.MANAGER}?status=Following&entity=Communities`
                        )
                      }
                    >
                      View all
                    </ButtonV3>
                  )}
                  <ButtonV3
                    className={styles.sectionButton}
                    variant="text"
                    onClick={() => router.push(PAGE_ROUTES.SOCIETIES)}
                  >
                    Explore more
                  </ButtonV3>
                </Row>
              )}
            </Row>
          ) : (
            <div className={styles.spinner}>
              <RightSidebarSkeleton />
            </div>
          )}
        </section>
        <section className={styles.section}>
          <Row className={styles.sectionHeader}>
            <p>Following</p>
          </Row>
          {!isFollowedCompaniesLoading ? (
            <Row className={styles.sectionwrap}>
              {followedCompanies?.length > 0 ? (
                followedCompanies.slice(0, 5).map((following, index) => (
                  <Row
                    align="start"
                    justify="space-between"
                    wrap={false}
                    columnGap={8}
                    className={styles.sectionRow}
                    key={index}
                    onClick={() =>
                      router.push(`${PAGE_ROUTES.COMPANIES}/${following.slug}`)
                    }
                  >
                    <section className={styles.sectionRowContainer}>
                      <Avatar
                        name={following.name}
                        icon={following.icon_url}
                        size="small"
                        className={styles.avatar}
                        altText={'company_logo'}
                      />
                      <p className={styles.sectionText}>{following.name}</p>
                    </section>
                    {following.unseen_count > 0 && (
                      <span className={styles.countText}>
                        <p>{following.unseen_count}</p>
                      </span>
                    )}
                  </Row>
                ))
              ) : (
                <Row className={cn(styles.emptyRow, styles.emptyRowFollowing)}>
                  <div className={styles.cont}>
                    <div>
                      <p className={styles.emptyHeadText}>
                        You&apos;re not following any companies
                      </p>
                      <p className={styles.emptyRowText}>
                        Follow companies to receive updates about new
                        opportunities
                      </p>
                      <p
                        className={cn(
                          styles.exploreButton,
                          styles.exploreButtonFollowing
                        )}
                        onClick={() =>
                          router.push(PAGE_ROUTES.COMPANIES_SEARCH)
                        }
                      >
                        Explore companies
                      </p>
                    </div>
                  </div>
                </Row>
              )}
              {/* societies list */}
              {followedCompanies?.length > 0 && (
                <Row className={styles.sectionLastRow}>
                  {followedCompanies?.length > 2 && (
                    <ButtonV3
                      className={styles.sectionButton}
                      variant="text"
                      onClick={() =>
                        router.push(
                          `${PAGE_ROUTES.MANAGER}?status=Following&entity=Companies`
                        )
                      }
                    >
                      View all
                    </ButtonV3>
                  )}
                  <ButtonV3
                    className={styles.sectionButton}
                    variant="text"
                    onClick={() => router.push(PAGE_ROUTES.COMPANIES)}
                  >
                    Explore more
                  </ButtonV3>
                </Row>
              )}
            </Row>
          ) : (
            <div className={styles.spinner}>
              <RightSidebarSkeleton />
            </div>
          )}
        </section>

        {appliedTouchpoints &&
          (appliedTouchpoints?.enabled?.jobs?.data?.length > 0 ||
            appliedTouchpoints?.enabled?.jobs?.data?.length > 0) && (
            <section className={styles.section}>
              <Row className={styles.sectionHeader}>
                <p>Applications</p>
              </Row>
              <Row className={styles.sectionwrap}>
                {appliedTouchpoints?.enabled.jobs.data
                  .concat(appliedTouchpoints?.enabled.internships.data)
                  .slice(0, 5)
                  .map((application, index) => (
                    <Row className={styles.sectionRow} key={index}>
                      <section
                        className={styles.sectionRowContainer}
                        onClick={() => {
                          const domain =
                            typeof window !== 'undefined'
                              ? window.location.origin
                              : process.env.NEXT_PUBLIC_SITE_URL;
                          router.push(
                            `${domain}${getOpportunityPublicPagePathname(
                              application.attributes.touchpointable_type,
                              application.attributes.slug
                            )}`
                          );
                        }}
                      >
                        <Avatar
                          name={application.attributes.title}
                          icon={
                            application?.attributes?.creatable_for.data
                              .attributes?.icon_url
                          }
                          altText={'company_logo'}
                          size="small"
                          className={styles.avatar}
                        />
                        <p className={styles.sectionText}>
                          {application.attributes.title}
                        </p>
                      </section>
                    </Row>
                  ))}
              </Row>
              {appliedTouchpoints?.enabled?.jobs?.data?.length &&
                appliedTouchpoints?.enabled?.jobs?.data?.length > 0 && (
                  <Row className={styles.sectionLastRow}>
                    {appliedTouchpoints?.enabled?.jobs?.data?.length +
                      appliedTouchpoints?.enabled?.internships.data.length >
                      2 && (
                      <ButtonV3
                        className={styles.sectionButton}
                        variant="text"
                        onClick={() =>
                          router.push(`${PAGE_ROUTES.MANAGER}?status=Applied`)
                        }
                      >
                        View all
                      </ButtonV3>
                    )}
                  </Row>
                )}
            </section>
          )}

        {acceptedTouchpoints &&
          acceptedTouchpoints?.enabled?.events?.data.length > 0 && (
            <section className={styles.section}>
              <Row className={styles.sectionHeader}>
                <p>Registrations</p>
              </Row>
              <Row className={styles.sectionwrap}>
                {acceptedTouchpoints?.enabled?.events?.data
                  .slice(0, 5)
                  .map((registration, index) => (
                    <Row
                      className={styles.sectionRow}
                      key={index}
                      onClick={() => {
                        const domain =
                          typeof window !== 'undefined'
                            ? window.location.origin
                            : process.env.NEXT_PUBLIC_SITE_URL;
                        router.push(
                          `${domain}${getOpportunityPublicPagePathname(
                            registration.attributes.touchpointable_type,
                            registration.attributes.slug
                          )}`
                        );
                      }}
                    >
                      <section className={styles.sectionRowContainer}>
                        <Avatar
                          name={registration.attributes.title}
                          icon={
                            registration?.attributes?.creatable_for.data
                              ?.attributes?.icon_url
                          }
                          altText={'society_logo'}
                          size="small"
                          className={styles.avatar}
                        />
                        <p
                          className={styles.sectionText}
                          style={{ width: '170px' }}
                        >
                          {registration.attributes.title}
                        </p>
                      </section>
                      {EventDate(registration.attributes.start_date) && (
                        <span className={styles.dateBadge}>
                          {' '}
                          <p>{EventDate(registration.attributes.start_date)}</p>
                        </span>
                      )}
                    </Row>
                  ))}
              </Row>
              {acceptedTouchpoints?.enabled?.events?.data.length > 0 && (
                <Row className={styles.sectionLastRow}>
                  {acceptedTouchpoints?.enabled?.events?.data.length > 0 && (
                    <ButtonV3
                      className={styles.sectionButton}
                      variant="text"
                      onClick={() =>
                        router.push(`${PAGE_ROUTES.MANAGER}?status=Registered`)
                      }
                    >
                      View all
                    </ButtonV3>
                  )}
                </Row>
              )}
            </section>
          )}
      </div>
    );
  }
);

SidebarRight.displayName = 'SidebarRight';
